import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { PrizeWeightFiltersStateService } from './prize-weight-filters-state.service';
import { PrizeDateFilterStateService } from './prize-date-filter-state.service';
import { PrizeTagFilterStateService } from './prize-tag-filter-state.service';
import { getMissingRequiredFilters } from './required-filters.util';

/**
 * The Filters section's submit guards, shared by every surface that can trigger a Save Filters /
 * Generate / Apply-to-next-rerun action against the same underlying filter state — originally
 * just GeneratePrizesPanelComponent's private blockedBySubmitGuards(), extracted here (CMS-6116)
 * so the Filters drawer enforces identical validation without duplicating the logic.
 */
@Injectable({
  providedIn: 'root',
})
export class PrizeFilterGuardsService {
  constructor(
    private messageService: MessageService,
    private weightFiltersState: PrizeWeightFiltersStateService,
    private dateFilterState: PrizeDateFilterStateService,
    private tagFilterState: PrizeTagFilterStateService,
  ) {}

  /**
   * Blocks the action on two independent guards, surfacing every applicable error as its own
   * toast in one pass — not just the first one found:
   *  1. Each weights section must total 100% (see PrizeWeightFiltersStateService).
   *  2. The minimum required filters must be set: a Date filter, a Tag filter, and at least one
   *     category per weight section (see getMissingRequiredFilters()).
   */
  blockedBySubmitGuards(): boolean {
    const unbalancedSections = this.weightFiltersState.getUnbalancedSections();
    unbalancedSections.forEach((section) => {
      this.messageService.add({
        severity: 'error',
        summary: 'Weights must total 100%',
        detail: `${section.label} must total 100% before filters can be saved or a new batch can be generated.`,
      });
    });

    const missingRequiredFilters = getMissingRequiredFilters({
      hasDateFilter: this.dateFilterState.hasAtLeastOneCondition(),
      hasTagFilter: this.tagFilterState.hasAtLeastOneRule(),
      weightSectionsMissingCategories: this.weightFiltersState.getSectionsMissingCategories(),
    });
    missingRequiredFilters.forEach((missing) => {
      this.messageService.add({
        severity: 'error',
        summary: 'Filters incomplete',
        detail: missing.message,
      });
    });

    return unbalancedSections.length > 0 || missingRequiredFilters.length > 0;
  }
}
